import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import decodePolyline from "decode-google-map-polyline";
import { useLazyQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button, EncodedPolyLine, FormGroup, Select, showSnackbar, MarkerProps } from "@cofano/cck";
import { Box } from "@mui/material";

import { FormErrorMessage } from "components/form/FormErrorMessage";
import DatePicker from "components/cck/DatePicker";
import DataFormat from "components/utils/DataFormat";

import { Language } from "utils";
import { FormattedDate } from "utils/DateUtils";

import ShipSelect from "view/ships/components/selects/ShipSelect";
import LocationSelect from "view/locations/components/selects/LocationSelect";

import { MapRouteType } from "../../utils/MapUtils";
import {
	mapLocationRouteSchema,
	MapLocationRouteSchemaInput,
	MapLocationRouteSchemaOutput,
	mapShipRouteSchema,
	MapShipRouteSchemaInput,
	MapShipRouteSchemaOutput,
} from "../../utils/map.schema";

import { GetBatchRoutesDocument, GetRouteWithCurrentPositionDocument } from "__generated__/view/maps/graphql/map.queries.generated";
import { RouteFragment } from "__generated__/view/maps/graphql/map.fragments.generated";

const MapRouteTap = ({ onChange }: {
	onChange: (markers: MarkerProps[], encodedPolylines: EncodedPolyLine[]) => void;
}) => {

	const [ routeType, setRouteType ] = useState<MapRouteType>(MapRouteType.Ship);

	const createMarkersAndPolylines = (route:  RouteFragment | undefined) => {

		const markers: MarkerProps[] = [];
		const encodedPolylines: EncodedPolyLine[] = [];

		if (route?.polyline) {
			const lines = decodePolyline(route.polyline);

			if (lines && lines.length > 0) {
				markers.push({
					lat: lines[0].lat,
					lng: lines[0].lng,
					component: () => <div />,
				});

				markers.push({
					lat: lines[lines.length - 1].lat,
					lng: lines[lines.length - 1].lng,
					component: () => <div />,
				});
			}

			encodedPolylines.push({
				color: "#1763A6",
				line: route.polyline,
			});
		}

		onChange(markers, encodedPolylines);
	};

	return (
		<Box p={2}>
			<FormGroup label={intl.formatMessage({ id: "SELECT_ROUTE_TYPE" })} labelPlacement="left-center">
				<Select
					onChange={(op) => {
						if (op) {
							setRouteType(op);
						}
					}}
					value={routeType}
					fetchOptions={Object.values(MapRouteType)}
					getOptionLabel={option => intl.formatMessage({ id: option })}
					clearable={false}
					disableSearch
				/>
			</FormGroup>

			{routeType === MapRouteType.Ship && (
				<ShipRouteForm createMarkersAndPolylines={createMarkersAndPolylines} />
			)}

			{routeType === MapRouteType.Location && (
				<LocationRouteForm createMarkersAndPolylines={createMarkersAndPolylines} />
			)}
		</Box>
	);
};

const ShipRouteForm = ({
	createMarkersAndPolylines,
}: {
	createMarkersAndPolylines: (data: RouteFragment) => void
 }) => {

	const [ response, setResponse ] = useState<RouteFragment | undefined>(undefined);

	const { handleSubmit, control } = useForm<MapShipRouteSchemaInput, unknown, MapShipRouteSchemaOutput>({
		resolver: zodResolver(mapShipRouteSchema),
		defaultValues: {
			ship: null,
			time: null,
			location: null,
		},
	});

	const [ fetchCurrentPositionRoutes, { loading } ] = useLazyQuery(GetRouteWithCurrentPositionDocument);

	const search = (data: MapShipRouteSchemaOutput) => {
		fetchCurrentPositionRoutes({
			variables: {
				shipId: data.ship.id,
				time: data.time,
				locationId: data.location.id,
			},
		}).then(({ data }) => {
			const response = data?.getRouteWithCurrentPosition;
			if (response) {
				createMarkersAndPolylines(response);
				setResponse(response);
			} else {
				setResponse(undefined);
			}
		});
	};

	return (
		<React.Fragment>
			<form onSubmit={handleSubmit(search)}>
				<FormGroup
					label={Language("SHIPS_SINGLE")}
					labelPlacement="left-center"
					required>
					<Controller
						name="ship"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<ShipSelect {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>

				<FormGroup
					label={Language("MAPS_START_TIME")}
					labelPlacement="left-center"
					required>
					<Controller
						name="time"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<DatePicker {...field} showTimeInput />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>

				<FormGroup
					label={Language("LOCATION")}
					labelPlacement="left-center"
					required>
					<Controller
						name="location"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<LocationSelect {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>

				<Button loading={loading} size="small" type="submit">
					{Language("SEARCH")}
				</Button>
			</form>

			{response && (
				<React.Fragment>
					<br />

					<FormGroup label={Language("DEPARTURE_TIME")} labelPlacement="left-center">
						<FormattedDate date={response.departureTime} />
					</FormGroup>

					<FormGroup label={Language("DISTANCE")} labelPlacement="left-center">
						{response.distanceKm && DataFormat.distance(response.distanceKm)}
					</FormGroup>

					<FormGroup label={Language("SAILING_TIME")} labelPlacement="left-center">
						{response.sailTimeMin && DataFormat.minutesToTime(response.sailTimeMin)}
					</FormGroup>

					<FormGroup label={Language("SLUICE_TIME")} labelPlacement="left-center">
						{response.sluiceTimeMin ? DataFormat.minutesToTime(response.sluiceTimeMin) : Language("STATUS_NOT_APPLICABLE")}
					</FormGroup>
				</React.Fragment>
			)}
		</React.Fragment>
	);
};

const LocationRouteForm = ({ createMarkersAndPolylines }: { createMarkersAndPolylines: (data: RouteFragment) => void }) => {

	const [ response, setResponse ] = useState<RouteFragment | undefined>(undefined);

	const { handleSubmit, control } = useForm<MapLocationRouteSchemaInput, unknown, MapLocationRouteSchemaOutput>({
		resolver: zodResolver(mapLocationRouteSchema),
		defaultValues: {
			from: null,
			to: null,
		},
	});

	const [ fetchBatchRoutes, { loading } ] = useLazyQuery(GetBatchRoutesDocument);

	const search = (data: MapLocationRouteSchemaOutput) => {
		fetchBatchRoutes({
			variables: {
				input: {
					routes: [ {
						reference: "location",
						from: data.from.id,
						to: data.to.id,
					} ],
				},
			},
		}).then( ({ data }) => {
			const locationRoute = data?.getBatchRoutes?.filter(r => r.reference === "location")[0]?.route;
			if (locationRoute) {
				setResponse(locationRoute);
				createMarkersAndPolylines(locationRoute);
			} else {
				showSnackbar(intl.formatMessage({ id: "NO_ROUTE_FOUND" }), { variant: "error" });
				setResponse(undefined);
			}
		});
	};

	return (
		<React.Fragment>
			<form onSubmit={handleSubmit(search)}>
				<FormGroup
					label={Language("FROM")}
					labelPlacement="left-center"
					required>
					<Controller
						name="from"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<LocationSelect {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>

				<FormGroup
					label={Language("TO")}
					labelPlacement="left-center"
					required>
					<Controller
						name="to"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<LocationSelect {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>

				<Button loading={loading} size="small" type="submit">
					{Language("SEARCH")}
				</Button>
			</form>

			{response && (
				<React.Fragment>
					<br />

					<FormGroup label={Language("DISTANCE")} labelPlacement="left-center">
						{response.distanceKm && DataFormat.distance(response.distanceKm)}
					</FormGroup>

					<FormGroup label={Language("SAILING_TIME")} labelPlacement="left-center">
						{response.sailTimeMin && DataFormat.minutesToTime(response.sailTimeMin)}
					</FormGroup>

					<FormGroup label={Language("SLUICE_TIME")} labelPlacement="left-center">
						{response.sluiceTimeMin ? DataFormat.minutesToTime(response.sluiceTimeMin) : Language("STATUS_NOT_APPLICABLE")}
					</FormGroup>
				</React.Fragment>
			)}
		</React.Fragment>
	);
};

export default MapRouteTap;
